import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import LoadingSpinner from '../LoadingSpinner';
import './DailyBlendSummary.css';

function DailyBlendSummary({ date: initialDate, onClose }) {
  const [selectedDate, setSelectedDate] = useState(initialDate || new Date().toISOString().split('T')[0]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const printRef = useRef();

  useEffect(() => {
    fetchDailySummary();
  }, [selectedDate]);

  const fetchDailySummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/blends/daily-summary/${selectedDate}`);
      setSummary(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching daily summary:', err);
      setError('Failed to load daily summary');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const originalContent = document.body.innerHTML;

    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContent;

    // Re-attach React after print
    window.location.reload();
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Group blends by melter for better organization
  const groupBlendsByMelter = () => {
    if (!summary?.blends_data) return {};

    const grouped = {};
    summary.blends_data.forEach(blend => {
      const melterName = blend.melters?.name || 'Unassigned';
      if (!grouped[melterName]) {
        grouped[melterName] = [];
      }
      grouped[melterName].push(blend);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="daily-summary-modal">
        <div className="daily-summary-container">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="daily-summary-modal">
        <div className="daily-summary-container">
          <div className="error-container">
            <div className="error-message">{error}</div>
            <button onClick={fetchDailySummary}>Retry</button>
            <button onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const groupedBlends = groupBlendsByMelter();

  return (
    <div className="daily-summary-modal">
      <div className="daily-summary-container">
        <div className="summary-actions">
          <div className="date-selector">
            <label>Select Date:</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="date-input"
            />
          </div>
          <div className="action-buttons">
            <button onClick={handlePrint} className="print-button">
              Print Summary
            </button>
            <button onClick={onClose} className="close-button">
              Close
            </button>
          </div>
        </div>

        <div ref={printRef} className="daily-summary" style={{ page: 'blend-summary' }}>
          <div className="summary-page">
            {/* Header */}
            <div className="summary-header">
              <h1>Daily Blend Production Summary</h1>
              <div className="summary-date">{formatDate(selectedDate)}</div>
            </div>

            {/* Overview Cards */}
            <div className="overview-section">
              <h2>Production Overview</h2>
              <div className="overview-cards">
                <div className="overview-card">
                  <div className="card-label">Total Blends</div>
                  <div className="card-value">{summary?.total_blends || 0}</div>
                </div>
                <div className="overview-card">
                  <div className="card-label">Total Wax</div>
                  <div className="card-value">{summary?.total_wax_lbs || 0} lbs</div>
                </div>
                <div className="overview-card">
                  <div className="card-label">Total Oil</div>
                  <div className="card-value">{summary?.total_oil_lbs || 0} lbs</div>
                </div>
                <div className="overview-card">
                  <div className="card-label">Total Units</div>
                  <div className="card-value">{summary?.total_units || 0}</div>
                </div>
              </div>
            </div>

            {/* Melter Breakdown */}
            {Object.keys(groupedBlends).length > 0 ? (
              <div className="melter-breakdown">
                <h2>Blends by Melter</h2>
                {Object.entries(groupedBlends).map(([melterName, blends]) => (
                  <div key={melterName} className="melter-group">
                    <h3 className="melter-group-title">
                      {melterName} ({blends.length} blend{blends.length !== 1 ? 's' : ''})
                    </h3>
                    <table className="blends-table">
                      <thead>
                        <tr>
                          <th>Blend #</th>
                          <th>Batch</th>
                          <th>Fragrance</th>
                          <th>Wax (lbs/in)</th>
                          <th>Oil (lbs)</th>
                          <th>Units</th>
                          <th>Status</th>
                          <th>Operator</th>
                        </tr>
                      </thead>
                      <tbody>
                        {blends.map(blend => (
                          <tr key={blend.id}>
                            <td className="blend-number">{blend.blend_number}</td>
                            <td>{blend.batch_name || '-'}</td>
                            <td>{blend.fragrance_oil_name}</td>
                            <td>
                              {blend.total_wax_lbs} /
                              <span className="highlight"> {blend.wax_inches || 'N/A'}</span>
                            </td>
                            <td>{blend.total_oil_lbs}</td>
                            <td>{blend.total_units}</td>
                            <td>
                              <span className={`status-badge status-${blend.status}`}>
                                {blend.status.replace('_', ' ')}
                              </span>
                            </td>
                            <td>{blend.operator_name || '-'}</td>
                          </tr>
                        ))}
                        <tr className="subtotal-row">
                          <td colSpan="3" className="subtotal-label">Subtotal:</td>
                          <td>{blends.reduce((sum, b) => sum + parseFloat(b.total_wax_lbs || 0), 0).toFixed(2)} lbs</td>
                          <td>{blends.reduce((sum, b) => sum + parseFloat(b.total_oil_lbs || 0), 0).toFixed(2)} lbs</td>
                          <td>{blends.reduce((sum, b) => sum + parseInt(b.total_units || 0), 0)}</td>
                          <td colSpan="2"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-blends">
                <p>No blends scheduled for {formatDate(selectedDate)}</p>
              </div>
            )}

            {/* Materials Summary */}
            {summary?.total_blends > 0 && (
              <div className="materials-summary">
                <h2>Total Materials Required</h2>
                <div className="materials-grid">
                  <div className="material-item">
                    <span className="material-label">Wax:</span>
                    <span className="material-value">{summary.total_wax_lbs} lbs</span>
                  </div>
                  <div className="material-item">
                    <span className="material-label">Fragrance Oil:</span>
                    <span className="material-value">{summary.total_oil_lbs} lbs</span>
                  </div>
                </div>

                {/* Fragrance Breakdown */}
                <div className="fragrance-breakdown">
                  <h3>Oil by Fragrance</h3>
                  <table className="fragrance-table">
                    <thead>
                      <tr>
                        <th>Fragrance</th>
                        <th>Total Oil (lbs)</th>
                        <th>Number of Blends</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const fragranceMap = {};
                        summary.blends_data?.forEach(blend => {
                          const fragrance = blend.fragrance_oil_name;
                          if (!fragranceMap[fragrance]) {
                            fragranceMap[fragrance] = {
                              oil: 0,
                              count: 0
                            };
                          }
                          fragranceMap[fragrance].oil += parseFloat(blend.total_oil_lbs || 0);
                          fragranceMap[fragrance].count += 1;
                        });

                        return Object.entries(fragranceMap).map(([fragrance, data]) => (
                          <tr key={fragrance}>
                            <td>{fragrance}</td>
                            <td>{data.oil.toFixed(2)}</td>
                            <td>{data.count}</td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="summary-footer">
              <div className="footer-text">
                Generated on {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DailyBlendSummary;